import React, { useEffect } from "react";
import img_logo from "../../img/logo_circles_only.png";
import OutboundLink from "../../components/OutboundLink";

/**
 * AboutModal — the v2-styled "About Plex" overlay (PLEX-4103, Release Notes
 * Slice D). Presentational only: open-state is owned by the parent
 * (AccountMenu), mirroring ReleaseNotesModal.
 *
 * Ports the content of the classic src/components/modals/AboutPlexModal.js —
 * same appStatusStore.versionRequirements / versionInfo data and the same
 * required-vs-given comparison colouring — into the v2 modal shell.
 *
 * Props:
 *   open                — whether the modal is visible
 *   onClose             — called on [×] / Escape / backdrop click
 *   versionRequirements — appStatusStore.versionRequirements
 *                         { templates:{required,given}, frontend:{required},
 *                           backend:{required,given}, data:{required,given} }
 *   versionInfo         — appStatusStore.versionInfo ({ tid, domains, pool_id })
 *   isAdmin             — gates the pool/tenant/domains rows
 */

// Ported verbatim from the classic AboutPlexModal so the required-vs-given
// status colouring stays identical across v1 and v2.
export const getClassFromVerComparison = (req = "", given = "") => {
  const [rMajor, rMinor, rParch] = req.split(".").map((x) => x.replace(/\D+/g, ""));
  const [gMajor, gMinor, gParch] = given.split(".").map((x) => x.replace(/\D+/g, ""));
  if (!(parseInt(rMajor, 10) <= parseInt(gMajor, 10) || given === "0.0.0")) {
    return "ver-error";
  }
  if (!(parseInt(rMinor, 10) <= parseInt(gMinor, 10))) {
    return "ver-warning";
  }
  if (parseInt(rParch, 10) <= parseInt(gParch, 10)) {
    return "ver-ok";
  }
  return "ver-outdated";
};

const AboutModal = ({
  open,
  onClose,
  versionRequirements = {},
  versionInfo = {},
  isAdmin = false,
}) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const {
    templates = {},
    frontend = {},
    backend = {},
    data = {},
  } = versionRequirements || {};
  const { tid: tenantID, domains, pool_id: userPoolID } = versionInfo || {};
  const tenantDomains = domains
    ? domains.filter((x) => x !== "plexresearch.com").join(", ")
    : "";

  const onBackdrop = (e) => { if (e.target === e.currentTarget) onClose?.(); };

  return (
    <div
      className="about-modal__backdrop"
      data-testid="v2-about-modal-backdrop"
      onClick={onBackdrop}
    >
      <div
        className="about-modal"
        data-testid="v2-about-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="about-modal-title"
      >
        <button
          type="button"
          className="about-modal__close"
          data-testid="v2-about-modal-close"
          aria-label="Dismiss about"
          onClick={() => onClose?.()}
        >
          ✕
        </button>

        <div className="about-modal__header">
          <img src={img_logo} alt="Plex Research logo" className="about-modal__logo" />
          <h2 className="about-modal__title" id="about-modal-title">Plex Search</h2>
        </div>

        <div className="about-modal__body">
          <div className="about-modal__rows" role="table" aria-label="Version information">
            <div className="about-row" role="row">
              <span className="about-row__label" role="cell">Version</span>
              <span className="about-row__val" role="cell">{frontend.required}</span>
              <span className="about-row__val" role="cell" />
            </div>
            <div className="about-row" role="row" data-testid="v2-about-row-api">
              <span
                className={`about-row__label ${getClassFromVerComparison(backend.required, backend.given)}`}
                role="cell"
              >
                API
              </span>
              <span className="about-row__val" role="cell">{backend.required}</span>
              <span className="about-row__val" role="cell">{backend.given}</span>
            </div>
            <div className="about-row" role="row" data-testid="v2-about-row-templates">
              <span
                className={`about-row__label ${getClassFromVerComparison(templates.required, templates.given)}`}
                role="cell"
              >
                UI Templates
              </span>
              <span className="about-row__val" role="cell">{templates.required}</span>
              <span className="about-row__val" role="cell">{templates.given}</span>
            </div>
            <div className="about-row" role="row" data-testid="v2-about-row-data">
              <span
                className={`about-row__label ${getClassFromVerComparison(data.required, data.given)}`}
                role="cell"
              >
                Datasets Release
              </span>
              <span className="about-row__val" role="cell">{data.required}</span>
              <span className="about-row__val" role="cell">{data.given}</span>
            </div>

            {isAdmin && (
              <div className="about-row about-row--admin" role="row" data-testid="v2-about-row-pool">
                <span className="about-row__label" role="cell">pool ID</span>
                <span className="about-row__val" role="cell">{userPoolID}</span>
              </div>
            )}
            {isAdmin && tenantID && (
              <div className="about-row about-row--admin" role="row" data-testid="v2-about-row-tenant">
                <span className="about-row__label" role="cell">Tenant ID</span>
                <span className="about-row__val" role="cell">{tenantID}</span>
              </div>
            )}
            {isAdmin && tenantDomains !== "" && (
              <div className="about-row about-row--admin" role="row" data-testid="v2-about-row-domains">
                <span className="about-row__label" role="cell">Domains</span>
                <span className="about-row__val" role="cell">{tenantDomains}</span>
              </div>
            )}
          </div>

          <div className="about-modal__legal">
            © {new Date().getFullYear()}{" "}
            <OutboundLink href="https://plexresearch.com/">Plex Research Inc</OutboundLink>
            <div>All rights reserved</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutModal;
